import * as React from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";

import { Button } from "components/common/buttons/Button";
import { useSidebar } from "components/common/Sidebar/Sidebar";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "components/common/Sheet";
import {
  APP_SIDEBAR_COLLAPSED_WIDTH_PX,
  APP_SIDEBAR_MOBILE_WIDTH_PX,
  APP_SIDEBAR_WIDTH_PX,
} from "components/layout/shell/sidebarShell";
import { useNavigationSearch } from "components/layout/shell/GlobalNavigationSearch";
import { cn } from "utils/cn";
import { AppSidebarLayoutProvider } from "./appSidebarLayout";
import { sidebarFocusRingClassName } from "./SidebarRow";

export interface AppSidebarProps {
  /** Accessible name of the `nav` landmark, e.g. "Project navigation". */
  label: string;
  /** Top block: the workspace switcher. */
  header: React.ReactNode;
  /** Scrollable navigation for the current area. */
  children: React.ReactNode;
  /** Bottom block: help, notifications, account. */
  footer: React.ReactNode;
}

const MOBILE_LAYOUT = { collapsed: false, device: "mobile" as const };

const SidebarBody = ({ label, header, children, footer }: AppSidebarProps) => (
  <nav
    aria-label={label}
    className="flex h-full min-h-0 w-full flex-col bg-chrome text-chrome-foreground"
  >
    <div className="flex shrink-0 flex-col gap-2 px-2 pb-1.5 pt-2.5">{header}</div>
    <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 pb-2 pt-1.5 [scrollbar-width:thin]">
      {children}
    </div>
    <div className="shrink-0 border-t border-chrome-border px-2 pb-2 pt-1.5">{footer}</div>
  </nav>
);

/**
 * The one app sidebar used in every context. On desktop it is 252px wide, or
 * 60px collapsed (cookie persisted, Cmd/Ctrl+B). Below md it opens as a sheet
 * from the header menu button and closes when the route changes.
 */
export const AppSidebar = (props: AppSidebarProps) => {
  const { isMobile, openMobile, setOpenMobile, state } = useSidebar();
  const { pathname } = useLocation();
  const collapsed = !isMobile && state === "collapsed";
  const desktopLayout = React.useMemo(
    () => ({ collapsed, device: "desktop" as const }),
    [collapsed]
  );

  // An action waiting for the sheet to finish closing (focus restored,
  // aria-hidden and pointer lock removed), e.g. opening the search palette.
  const afterSheetClose = React.useRef<(() => void) | null>(null);
  const navigationSearch = useNavigationSearch();
  const setSearchGate = navigationSearch?.setOpenGate;
  const sheetOpen = isMobile && openMobile;

  // While the sheet is open, every palette open (any trigger or Cmd/Ctrl+K)
  // closes it first: a palette over a modal sheet would be aria-hidden,
  // unclickable, and lose focus to the menu button.
  React.useEffect(() => {
    if (!setSearchGate || !sheetOpen) return;
    setSearchGate((openSearch) => {
      afterSheetClose.current = openSearch;
      setOpenMobile(false);
    });
    return () => setSearchGate(null);
  }, [setOpenMobile, setSearchGate, sheetOpen]);

  React.useEffect(() => {
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent
          side="left"
          data-slot="app-sidebar"
          data-mobile="true"
          className="max-w-[85vw] gap-0 border-r border-chrome-border bg-chrome p-0 [&>button]:hidden"
          style={{ width: APP_SIDEBAR_MOBILE_WIDTH_PX }}
          onCloseAutoFocus={(event) => {
            const action = afterSheetClose.current;
            if (!action) return;
            afterSheetClose.current = null;
            // The action decides where focus goes, not the menu button.
            event.preventDefault();
            action();
          }}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{props.label}</SheetTitle>
            <SheetDescription>Move between pages and projects.</SheetDescription>
          </SheetHeader>
          <AppSidebarLayoutProvider value={MOBILE_LAYOUT}>
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex shrink-0 justify-end px-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setOpenMobile(false)}
                  aria-label="Close navigation"
                  className={cn(
                    "h-8 w-8 rounded-md p-0 text-chrome-muted-foreground hover:text-chrome-foreground",
                    sidebarFocusRingClassName
                  )}
                >
                  <X aria-hidden />
                </Button>
              </div>
              <div className="min-h-0 flex-1">
                <SidebarBody {...props} />
              </div>
            </div>
          </AppSidebarLayoutProvider>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      data-slot="app-sidebar"
      data-state={state}
      className="relative z-20 hidden h-svh shrink-0 overflow-hidden bg-chrome transition-[width] duration-200 ease-out motion-reduce:transition-none md:flex"
      style={{ width: collapsed ? APP_SIDEBAR_COLLAPSED_WIDTH_PX : APP_SIDEBAR_WIDTH_PX }}
    >
      <AppSidebarLayoutProvider value={desktopLayout}>
        <SidebarBody {...props} />
      </AppSidebarLayoutProvider>
    </aside>
  );
};
